import { useState, useRef, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight, Twitter, Send, Copy, Ticket } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import { waitlistApi } from "@/lib/waitlistApi";
import { WaitlistCounter } from "@/components/lander/WaitlistCounter";
import { FoundingOffer } from "@/components/lander/FoundingOffer";
import { getFomoValue } from "@/lib/fomoStore";
import { getStoredInvite } from "@/lib/referral";
import { TURNSTILE_SITE_KEY } from "@/config";
import { MilestoneShareCard } from "@/components/lander/MilestoneShareCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const SITE_KEY = TURNSTILE_SITE_KEY;
const SHARE_BASE = process.env.REACT_APP_SHARE_BASE || "https://bloqtalk.app";
// Remembers a visitor who already joined so we can resume their LIVE referral standing on return.
const WL_STORE = "bloq_wl_me";

// Waitlist referral milestones — celebrated on the success card to supercharge sharing.
const REFERRAL_MILESTONES = [
  { n: 3, name: "Connector", emoji: "🔗" },
  { n: 10, name: "Amplifier", emoji: "📣" },
  { n: 25, name: "Founding Sharer", emoji: "👑" },
];
const MS_STORE = "bloq_wl_ms"; // per-email map of the highest milestone tier already celebrated
const reachedMilestone = (count) => [...REFERRAL_MILESTONES].reverse().find((m) => count >= m.n) || null;
const nextMilestone = (count) => REFERRAL_MILESTONES.find((m) => count < m.n) || null;
const celebratedTier = (email) => {
  try {
    return JSON.parse(localStorage.getItem(MS_STORE) || "{}")[email] || 0;
  } catch {
    return 0;
  }
};
const markCelebrated = (email, n) => {
  try {
    const m = JSON.parse(localStorage.getItem(MS_STORE) || "{}");
    m[email] = n;
    localStorage.setItem(MS_STORE, JSON.stringify(m));
  } catch {
    /* ignore */
  }
};

// Self-contained confetti burst (the public lander has no TipCelebration provider).
// Reuses the global .tip-confetti-piece keyframes from index.css.
function MilestoneConfetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 44 }, () => ({
        left: Math.random() * 100,
        color: ["#01fabd", "#7c5cff", "#ffd166", "#ff6b9a", "#ffffff"][Math.floor(Math.random() * 5)],
        w: 6 + Math.random() * 6,
        h: 8 + Math.random() * 8,
        drift: (Math.random() - 0.5) * 260,
        rot: Math.random() * 720,
        dur: 1.8 + Math.random() * 1.4,
        delay: Math.random() * 0.35,
        round: Math.random() > 0.6,
      })),
    [],
  );
  return (
    <div className="pointer-events-none fixed inset-0 z-[70] overflow-hidden" aria-hidden data-testid="milestone-confetti">
      {pieces.map((p, i) => (
        <span
          key={i}
          className="tip-confetti-piece"
          style={{
            left: `${p.left}%`,
            background: p.color,
            width: `${p.w}px`,
            height: `${p.h}px`,
            borderRadius: p.round ? "9999px" : "2px",
            "--tc-x": `${p.drift}px`,
            "--tc-rot": `${p.rot}deg`,
            "--tc-dur": `${p.dur}s`,
            "--tc-delay": `${p.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

function refFromUrl() {
  try {
    return new URLSearchParams(window.location.search).get("ref") || null;
  } catch {
    return null;
  }
}

// In-app invite links carry the inviter's account code as ?invite=BLOQ-XXXXXX.
// Pre-launch we funnel that into the waitlist as the referral attribution.
function inviteFromUrl() {
  try {
    return new URLSearchParams(window.location.search).get("invite") || null;
  } catch {
    return null;
  }
}

// Flush any locally-cached signups (captured while the API was unreachable) into the real endpoint.
let drainStarted = false;
async function drainQueue() {
  if (drainStarted) return;
  drainStarted = true;
  let q;
  try {
    q = JSON.parse(localStorage.getItem("bloq_waitlist_queue") || "[]");
  } catch {
    q = [];
  }
  if (!Array.isArray(q) || q.length === 0) return;
  const remaining = [];
  for (const item of q) {
    const payload = typeof item === "string" ? { email: item, source: "/queue" } : item;
    try {
      await waitlistApi.join(payload);
    } catch {
      remaining.push(item);
    }
  }
  if (remaining.length) localStorage.setItem("bloq_waitlist_queue", JSON.stringify(remaining));
  else localStorage.removeItem("bloq_waitlist_queue");
}

/**
 * Shared waitlist capture used by every lander variant.
 * Posts to the shared backend (POST /api/waitlist) and surfaces the returned
 * queue position + referral code for the "share to jump the line" loop.
 * If the endpoint is unreachable, the email is cached locally and drained later.
 */
export function WaitlistForm({
  className = "",
  inputClassName = "",
  buttonClassName = "",
  buttonLabel = "Join the Waitlist",
  placeholder = "you@wallet.eth",
  onDone,
  compact = false,
  stacked = false,
  showSuccess = true,
  showCounter = true,
  founding = false,
  refCode = "",
}) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done
  const [token, setToken] = useState(null);
  const [result, setResult] = useState(null);
  // The exact on-screen counter value at the instant the visitor hit Submit. Frozen here so a
  // background counter refresh (poll/tween) can never shift their assigned position afterwards.
  const [seenCount, setSeenCount] = useState(null);
  // True when the success view was restored from a prior visit (vs a fresh join) — lets us offer a reset.
  const [resumed, setResumed] = useState(false);
  // Milestone celebration state + the email whose referral standing is currently shown.
  const [celebrateMs, setCelebrateMs] = useState(false);
  // The tier just crossed — auto-opens the share-card modal at the peak "unlock" moment (null = closed).
  const [celebrateTier, setCelebrateTier] = useState(null);
  const [activeEmail, setActiveEmail] = useState(null);
  const [friend, setFriend] = useState("");
  const widgetRef = useRef(null);
  // Referral attribution: explicit prop (invite pages) → ?invite= in the URL → persisted invite code.
  // Ensures the inviting beta tester still gets credited when their link lands someone on the waitlist.
  const urlInvite = inviteFromUrl();
  const invitedCode = refCode || urlInvite || getStoredInvite();

  useEffect(() => {
    drainQueue();
    // Resume progress: if this visitor already joined, show their LIVE referral standing on return
    // (friends referred, spots climbed, current position) instead of the empty form — this is what
    // keeps the referral loop spreading. Only on the primary lander hero (showSuccess + live counter).
    if (!showSuccess || !showCounter) return;
    let stored;
    try {
      stored = JSON.parse(localStorage.getItem(WL_STORE) || "null");
    } catch {
      stored = null;
    }
    if (!stored?.email) return;
    waitlistApi
      .status(stored.email)
      .then((st) => {
        if (st && typeof st.position === "number") {
          setResult(st);
          setResumed(true);
          setActiveEmail(stored.email);
          setState("done");
        }
      })
      .catch(() => {});
  }, [showSuccess, showCounter]);

  // Celebrate crossing a referral milestone (3 / 10 / 25) once per email — badge pop + confetti + toast.
  useEffect(() => {
    if (state !== "done" || !showSuccess || !activeEmail) return;
    const count = result?.referral_count ?? 0;
    const reached = reachedMilestone(count);
    if (!reached || reached.n <= celebratedTier(activeEmail)) return;
    markCelebrated(activeEmail, reached.n);
    const spots = result?.spots_per_referral ?? 10;
    toast.success(`${reached.emoji} Milestone unlocked: ${reached.name}!`, {
      description: `${count} friends joined with your link — you've jumped ${(count * spots).toLocaleString()} spots up the line.`,
    });
    setCelebrateMs(true);
    setCelebrateTier(reached);
    const t = setTimeout(() => setCelebrateMs(false), 3600);
    return () => clearTimeout(t);
  }, [result, state, showSuccess, activeEmail]);

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value.includes("@") || value.length < 5) {
      toast.error("Please enter a valid email");
      return;
    }
    if (SITE_KEY && !token) {
      toast.error("Please complete the verification");
      return;
    }
    setState("loading");
    // Capture the number the visitor is looking at BEFORE the async join (defeats the count-up race).
    setSeenCount(getFomoValue());
    const payload = { email: value, source: window.location.pathname, ref: refFromUrl() || invitedCode, turnstile_token: token };
    try {
      const res = await waitlistApi.join(payload);
      setResult(res);
      setActiveEmail(value);
      setState("done");
      try {
        localStorage.setItem(WL_STORE, JSON.stringify({ email: value, code: res?.referral_code || "" }));
      } catch {
        /* ignore */
      }
      onDone?.(res);
    } catch {
      // Fallback: cache locally so no lead/UX is lost if the API is briefly unreachable.
      let q;
      try {
        q = JSON.parse(localStorage.getItem("bloq_waitlist_queue") || "[]");
      } catch {
        q = [];
      }
      if (!q.some((x) => (typeof x === "string" ? x : x.email) === value)) q.push(payload);
      localStorage.setItem("bloq_waitlist_queue", JSON.stringify(q));
      setState("done");
      onDone?.(null);
    }
  };

  const share = (network) => {
    const code = result?.referral_code;
    const link = code ? `${SHARE_BASE}/?ref=${code}` : SHARE_BASE;
    const text = encodeURIComponent("I just joined the BLOQ Talk waitlist — the trust-gated messenger for Web3. Get early access:");
    const url = encodeURIComponent(link);
    if (network === "x") window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, "_blank");
    else if (network === "tg") window.open(`https://t.me/share/url?url=${url}&text=${text}`, "_blank");
    else {
      navigator.clipboard?.writeText(link);
      toast.success("Referral link copied");
    }
  };

  // Invite a specific friend by email or phone. Opens the visitor's own mail/SMS
  // composer prefilled with their referral link (no server-side sending).
  const sendInvite = (e) => {
    e.preventDefault();
    const v = friend.trim();
    if (!v) return;
    const code = result?.referral_code;
    const link = code ? `${SHARE_BASE}/?ref=${code}` : SHARE_BASE;
    const msg = `I just joined the BLOQ Talk waitlist — the trust-gated messenger for Web3. Get early access: ${link}`;
    const isEmail = v.includes("@");
    window.location.href = isEmail
      ? `mailto:${encodeURIComponent(v)}?subject=${encodeURIComponent("You're invited to BLOQ Talk")}&body=${encodeURIComponent(msg)}`
      : `sms:${encodeURIComponent(v)}?body=${encodeURIComponent(msg)}`;
    toast.success(`Opening your ${isEmail ? "email" : "messages"} to invite ${v}`);
    setFriend("");
  };

  // Let a resumed visitor switch to a different email (clears the remembered signup).
  const startOver = () => {
    try {
      localStorage.removeItem(WL_STORE);
    } catch {
      /* ignore */
    }
    setResult(null);
    setResumed(false);
    setActiveEmail(null);
    setCelebrateMs(false);
    setCelebrateTier(null);
    setEmail("");
    setState("idle");
  };

  if (state === "done" && showSuccess) {
    // Position comes from the backend's real waitlist total (the SAME field the on-screen counter shows),
    // so it's always accurate — including on invite pages where the live counter isn't rendered (there the
    // FOMO store is empty and would otherwise fall back to the seed base). Only when the API response is
    // unavailable (offline queue path) do we fall back to the frozen counter value + 1.
    // Prefer the visitor's live rank (present on both the join response and the resumed status), then the
    // raw total, then the counter fallback. On a resume this reflects how far they've CLIMBED via referrals.
    const apiPos = result?.position ?? result?.total;
    const pos = typeof apiPos === "number" ? apiPos : (typeof seenCount === "number" ? seenCount : getFomoValue()) + 1;
    const spots = result?.spots_per_referral ?? 10;
    const refCount = result?.referral_count ?? 0;
    const code = result?.referral_code;
    const shareLink = code ? `${SHARE_BASE}/?ref=${code}` : SHARE_BASE;
    const reachedTier = reachedMilestone(refCount);
    return (
      <div
        className={cn("rounded-2xl border border-bloq-mint/40 bg-bloq-mint/10 p-6 text-center", className)}
        data-testid="waitlist-success"
      >
        <div className="text-sm text-bloq-muted">{resumed ? "Welcome back — your spot" : "You're on the list — position"}</div>
        <div className="font-heading text-4xl font-bold bloq-gradient-text" data-testid="waitlist-position">
          #{pos.toLocaleString()}
        </div>

        <div className="mt-4 rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-left" data-testid="waitlist-referral-progress">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-bloq-muted">Friends joined with your link</span>
            <span className="font-heading text-xl font-bold text-bloq-mint" data-testid="waitlist-referral-count">{refCount.toLocaleString()}</span>
          </div>
          <p className="mt-1.5 text-xs text-bloq-mint" data-testid="waitlist-referral-progress-msg">
            {refCount > 0
              ? `🚀 You've jumped ${(refCount * spots).toLocaleString()} spots up the line — keep sharing, each friend moves you up ${spots} more.`
              : `Invite friends — each one who joins moves you up ${spots} spots.`}
          </p>
        </div>

        {celebrateMs && <MilestoneConfetti />}
        <div className="mt-3 flex flex-col items-center gap-2" data-testid="waitlist-milestones">
          <div className="flex flex-wrap items-center justify-center gap-1.5">
            {REFERRAL_MILESTONES.map((m) => {
              const done = refCount >= m.n;
              const justHit = done && reachedMilestone(refCount)?.n === m.n && celebrateMs;
              return (
                <span
                  key={m.n}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition-all",
                    done ? "border-bloq-mint/40 bg-bloq-mint/10 text-bloq-mint" : "border-white/10 bg-white/[0.03] text-bloq-muted",
                    justHit && "animate-in zoom-in-50 duration-500",
                  )}
                  data-testid={`milestone-${m.n}`}
                >
                  <span>{m.emoji}</span>
                  {m.name}
                  {!done && <span className="opacity-60">· {m.n}</span>}
                </span>
              );
            })}
          </div>
          {nextMilestone(refCount) ? (
            <p className="text-xs text-bloq-muted" data-testid="milestone-next">
              {nextMilestone(refCount).n - refCount} more {nextMilestone(refCount).n - refCount === 1 ? "friend" : "friends"} to unlock {nextMilestone(refCount).emoji} {nextMilestone(refCount).name}
            </p>
          ) : (
            <p className="text-xs text-bloq-mint" data-testid="milestone-next">👑 Top tier — you're a Founding Sharer!</p>
          )}
        </div>

        {reachedTier && code && (
          <MilestoneShareCard
            milestone={reachedTier}
            code={code}
            refCount={refCount}
            spots={spots}
            position={pos}
            link={shareLink}
          />
        )}

        {/* Peak-moment share prompt — auto-opens the instant a tier is unlocked (once per email). */}
        <Dialog open={!!celebrateTier} onOpenChange={(o) => !o && setCelebrateTier(null)}>
          <DialogContent className="max-w-sm border-white/10 bg-bloq-s1 text-white" data-testid="milestone-celebrate-dialog">
            <DialogHeader>
              <DialogTitle className="text-center font-heading text-2xl font-black">
                <span className="mr-1 text-3xl">{celebrateTier?.emoji}</span>
                You unlocked {celebrateTier?.name}!
              </DialogTitle>
            </DialogHeader>
            <p className="text-center text-sm text-bloq-muted" data-testid="milestone-celebrate-subtitle">
              {refCount} friend{refCount === 1 ? "" : "s"} joined with your link{refCount > 0 ? ` — you've jumped ${(refCount * spots).toLocaleString()} spots.` : "."} Post your card and climb even faster.
            </p>
            {celebrateTier && code && (
              <MilestoneShareCard
                milestone={celebrateTier}
                code={code}
                refCount={refCount}
                spots={spots}
                position={pos}
                link={shareLink}
              />
            )}
          </DialogContent>
        </Dialog>

        {founding && (
          <div className="mt-3 rounded-xl border border-bloq-mint/30 bg-bloq-mint/[0.08] px-3 py-2 text-xs font-medium text-bloq-mint" data-testid="founding-reserved">
            🎉 Founding perks reserved — 50% off BLOQ+ for life + the exclusive Founding badge, unlocked at launch.
          </div>
        )}

        {code && (
          <div className="mt-5 space-y-3 text-left">
            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-bloq-muted">Your referral code</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 truncate rounded-xl border border-white/12 bg-black/40 px-4 py-2.5 font-mono text-sm tracking-widest text-white" data-testid="waitlist-referral-code">
                  {code}
                </div>
                <button
                  type="button"
                  onClick={() => { navigator.clipboard?.writeText(code); toast.success("Referral code copied"); }}
                  className="flex h-11 items-center gap-1.5 rounded-xl border border-white/12 px-3 text-sm font-medium text-bloq-mint transition-colors hover:bg-bloq-mint/10"
                  data-testid="waitlist-copy-code"
                >
                  <Copy className="h-4 w-4" /> Copy
                </button>
              </div>
            </div>

            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-bloq-muted">Invite a friend</div>
              <form onSubmit={sendInvite} className="flex items-center gap-2" data-testid="waitlist-invite-form">
                <input
                  value={friend}
                  onChange={(e) => setFriend(e.target.value)}
                  placeholder="Friend's email or phone"
                  className="h-11 flex-1 rounded-xl border border-white/12 bg-black/40 px-4 text-sm text-white outline-none transition-colors placeholder:text-bloq-muted focus:border-bloq-mint/50"
                  data-testid="waitlist-invite-input"
                />
                <button
                  type="submit"
                  className="flex h-11 items-center gap-1.5 rounded-xl bloq-gradient px-4 text-sm font-bold text-bloq-bg transition-opacity hover:opacity-90"
                  data-testid="waitlist-invite-send"
                >
                  <Send className="h-4 w-4" /> Invite
                </button>
              </form>
            </div>
          </div>
        )}

        <div className="mt-5 text-xs text-bloq-muted">or share your link</div>
        <div className="mt-2 flex items-center justify-center gap-3">
          <button onClick={() => share("x")} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/12 hover:bg-white/5" data-testid="waitlist-share-x"><Twitter className="h-5 w-5" /></button>
          <button onClick={() => share("tg")} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/12 hover:bg-white/5" data-testid="waitlist-share-tg"><Send className="h-5 w-5" /></button>
          <button onClick={() => share("copy")} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/12 hover:bg-white/5" data-testid="waitlist-share-copy"><Copy className="h-5 w-5" /></button>
        </div>

        {resumed && (
          <button type="button" onClick={startOver} className="mt-5 text-xs text-bloq-muted underline underline-offset-2 transition-colors hover:text-white" data-testid="waitlist-reset">
            Not you? Join with a different email
          </button>
        )}
      </div>
    );
  }

  if (state === "done") return null;

  return (
    <div className={cn("w-full", className)}>
      {urlInvite && !compact && (
        <div className="mb-5 flex items-center justify-center gap-2 rounded-full border border-bloq-purple/30 bg-bloq-purple/10 px-4 py-2 text-sm text-white" data-testid="waitlist-invite-banner">
          <Ticket className="h-4 w-4 shrink-0 text-bloq-purple" />
          <span>You've been invited — join the list to claim your spot.</span>
        </div>
      )}
      {showCounter && !compact && <WaitlistCounter className="mb-8" />}
      {founding && !compact && <FoundingOffer className="mb-6" />}
      <form onSubmit={submit} className={cn("flex w-full gap-2.5", stacked ? "flex-col" : compact ? "flex-row" : "flex-col sm:flex-row")} data-testid="waitlist-form">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={placeholder}
          className={cn(
            "h-13 flex-1 rounded-full border border-white/12 bg-black/40 px-5 py-3.5 text-white outline-none transition-colors placeholder:text-bloq-muted focus:border-bloq-mint/50",
            inputClassName,
          )}
          data-testid="waitlist-email-input"
        />
        <button
          type="submit"
          disabled={state === "loading" || (SITE_KEY && !token)}
          className={cn(
            "flex h-13 items-center justify-center gap-1.5 rounded-full bloq-gradient px-7 py-3.5 font-bold text-bloq-bg transition-opacity hover:opacity-90 disabled:opacity-60",
            stacked && "w-full",
            buttonClassName,
          )}
          data-testid="waitlist-submit-btn"
        >
          {state === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{buttonLabel}<ArrowRight className="h-4 w-4" /></>}
        </button>
      </form>

      {SITE_KEY && (
        <div className={cn("mt-3 flex", stacked ? "justify-center" : compact ? "justify-start" : "justify-center sm:justify-start")}>
          <Turnstile
            ref={widgetRef}
            siteKey={SITE_KEY}
            options={{ theme: "dark", size: "flexible" }}
            onSuccess={(t) => setToken(t)}
            onExpire={() => {
              setToken(null);
              widgetRef.current?.reset();
            }}
            onError={() => setToken(null)}
            data-testid="waitlist-turnstile"
          />
        </div>
      )}
    </div>
  );
}
